import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { STATIC_REVIEWS } from '../data/TestimonialsData';
import './Testimonials.css';

const AVATAR_COLORS = ['#3e7e6a','#2563eb','#d4a437','#7c3aed','#0891b2','#be185d','#b45309','#059669','#dc2626','#0f766e'];

/* Static reviews used as fallback only when DB returns nothing */
const ROW1_STATIC = [...STATIC_REVIEWS.slice(0, 5), ...STATIC_REVIEWS.slice(0, 5)];
const ROW2_STATIC = [...STATIC_REVIEWS.slice(5), ...STATIC_REVIEWS.slice(5)];

const StarRow = ({ count }) => (
    <div className="testi-stars">
        {Array.from({ length: count }).map((_, i) => (
            <i key={i} className="fa-solid fa-star" />
        ))}
    </div>
);

const Card = ({ review }) => (
    <div className="testi-card">
        <StarRow count={review.stars} />
        <p className="testi-text">{review.text}</p>
        <div className="testi-author">
            <div
                className="testi-avatar"
                style={{ background: review.color }}
            >
                {review.avatar}
            </div>
            <div className="testi-author-info">
                <span className="testi-author-name">{review.name}</span>
                <span className="testi-author-meta">
                    <i className="fa-solid fa-location-dot" />
                    {review.meta}
                </span>
            </div>
        </div>
    </div>
);

const mapReviews = (data) =>
    (data || []).map((item, i) => ({
        name: item.name,
        meta: item.visited,
        avatar: item.name?.slice(0, 2).toUpperCase() || '??',
        color: AVATAR_COLORS[i % AVATAR_COLORS.length],
        stars: item.rating,
        text: `"${item.message}"`,
    }));

const Testimonials = () => {
    const [dbReviews, setDbReviews] = useState([]);
    const [loaded, setLoaded] = useState(false);

    const fetchReviews = async () => {
        const { data } = await supabase
            .from('feedback')
            .select('*')
            .eq('show_on_homepage', true)
            .order('created_at', { ascending: false })
            .limit(10);
        setDbReviews(mapReviews(data));
        setLoaded(true);
    };

    useEffect(() => {
        fetchReviews();

        // Real-time: re-fetch whenever any feedback row changes (visibility toggled, new entry, delete)
        const channel = supabase
            .channel('feedback-homepage')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'feedback' }, () => {
                fetchReviews();
            })
            .subscribe();

        return () => { supabase.removeChannel(channel); };
    }, []);

    // Before DB loads: show static fallback so page doesn't flash empty
    // After DB loads: ONLY show reviews marked visible in admin — no static fallback
    const reviews = loaded ? dbReviews : STATIC_REVIEWS.map((r, i) => ({ ...r, color: AVATAR_COLORS[i % AVATAR_COLORS.length] }));
    const mid = Math.ceil(reviews.length / 2);
    const ROW1 = reviews.length >= 2 ? [...reviews.slice(0, mid), ...reviews.slice(0, mid)] : ROW1_STATIC;
    const ROW2 = reviews.length >= 2 ? [...reviews.slice(mid), ...reviews.slice(mid)] : ROW2_STATIC;

    // If DB has loaded and no reviews are marked visible, hide the entire section
    if (loaded && dbReviews.length === 0) return null;

    return (
    <section className="testi-section">
        {/* Header */}
        <div className="testi-header">
            <div className="testi-pill">
                <i className="fa-solid fa-quote-left" />
                What Our Clients Say
            </div>
            <h2 className="testi-title">
                Trusted by <span>15,000+</span> Happy Travellers
            </h2>
            <p className="testi-sub">
                Real stories from real people who let us craft their perfect journey.
            </p>
        </div>

        {/* Marquee rows */}
        <div className="testi-track-wrap">
            <div className="testi-row testi-row-1">
                {ROW1.map((r, i) => <Card key={i} review={r} />)}
            </div>
            <div className="testi-row testi-row-2">
                {ROW2.map((r, i) => <Card key={i} review={r} />)}
            </div>
        </div>

        {/* Trust stats */}
        <div className="testi-trust">
            <div className="testi-trust-item">
                <span className="testi-trust-num">15,000+</span>
                <span className="testi-trust-lbl">Happy Travellers</span>
            </div>
            <div className="testi-trust-divider" />
            <div className="testi-trust-item">
                <span className="testi-trust-num">4.9 ★</span>
                <span className="testi-trust-lbl">Average Rating</span>
            </div>
            <div className="testi-trust-divider" />
            <div className="testi-trust-item">
                <span className="testi-trust-num">12 yrs</span>
                <span className="testi-trust-lbl">In Business</span>
            </div>
            <div className="testi-trust-divider" />
            <div className="testi-trust-item">
                <span className="testi-trust-num">98%</span>
                <span className="testi-trust-lbl">Would Recommend</span>
            </div>
        </div>
    </section>
    );
};

export default Testimonials;
